import React, { useCallback, useState } from "react";

import {
  Box,
  colors,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { BsChevronDown, BsChevronUp, BsEyeFill, BsFillPencilFill, BsTrash3Fill } from "react-icons/bs";
var _ = require("lodash");
export enum columnTypes {
  EDIT = "EDIT",
  DELETE = "DELETE",
  DETAILS = "DETAILS",
}
export interface ColumnInterface {
  label: string;
  key: string;
  type?: columnTypes;
  minWidth?: string;
  sorting?: any;
  skipSorting?: boolean;
  rowRender?: (row: any) => string | React.ReactNode;
  actionFn?: (info: any) => void;
  textAlign?: "right" | "left" | "center" | "inherit" | "justify" | undefined;
  /** Stays in view while the other columns scroll under it. At most one a side. */
  sticky?: "left" | "right";
}
interface PropsInterface {
  loading: boolean;
  rows: Array<any>;
  columns: Array<ColumnInterface>;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  total: number;
  onDelete?: (id: number) => void;
  onSort?: (sorting: any) => void;
  initialSorting?: any;
  /**
   * Opens a row from a click anywhere on it. A click on a control in the row stays
   * that control's, and finishing a text selection opens nothing, so a number can
   * still be copied out of the table.
   */
  onRowClick?: (row: any) => void;
  /** One line a row, tighter cells, and the pager only underneath. For a wide table in its own card. */
  compact?: boolean;
  /** What a compact table says when it has no rows. */
  emptyText?: string;
}

const STICKY_CLASS = { left: "table-sticky-left", right: "table-sticky-right" };

const COMPACT_SX = {
  // Opaque, or the scrolling columns would show through. The shadow is what says they pass underneath.
  [`& .${STICKY_CLASS.left}, & .${STICKY_CLASS.right}`]: {
    position: "sticky",
    zIndex: 1,
    backgroundColor: "#ffffff",
  },
  [`& .${STICKY_CLASS.left}`]: {
    left: 0,
    boxShadow: "inset -1px 0 0 #eef1f5, 6px 0 8px -6px rgba(15, 23, 42, 0.1)",
  },
  [`& .${STICKY_CLASS.right}`]: {
    right: 0,
    boxShadow: "inset 1px 0 0 #eef1f5, -6px 0 8px -6px rgba(15, 23, 42, 0.1)",
  },
  [`& .MuiTableCell-head.${STICKY_CLASS.left}, & .MuiTableCell-head.${STICKY_CLASS.right}`]: {
    zIndex: 3,
    backgroundColor: "#f8fafc",
  },
  [`& .MuiTableRow-hover:hover .${STICKY_CLASS.left}, & .MuiTableRow-hover:hover .${STICKY_CLASS.right}`]: {
    backgroundColor: "#f8fafc",
  },
  "& .MuiTableRow-hover:hover": { backgroundColor: "#f8fafc" },
  "& .MuiTableCell-root": { borderColor: "#eef1f5", px: { xs: 1.25, sm: 2 } },
  "& .MuiTableCell-head": {
    color: "text.secondary",
    fontSize: "0.75rem",
    letterSpacing: "0.02em",
    py: 1.25,
    whiteSpace: "nowrap",
  },
  "& .MuiTableCell-body": { py: 1.25, fontSize: "0.875rem", whiteSpace: "nowrap" },
  "& tbody tr:last-of-type td": { borderBottom: 0 },
};

const CustomTable = (props: PropsInterface) => {
  const [sorting, setSorting] = useState<any>(props.initialSorting ?? {});
  const handleChangePage = (event: unknown, newPage: number) => {
    props.setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    props.setRowsPerPage(parseInt(event.target.value, 10));
    props.setPage(0);
  };

  const handleSort = useCallback((column: ColumnInterface) => {
    if (sorting === null || !sorting[column.key]) {
      setSorting({[column.key]: 'asc'});
      if (props.onSort) {
        const sortingDefinition = column.sorting ? column.sorting('asc') : {[column.key]: 'asc'};
        props.onSort(sortingDefinition);
      }
      return;
    }
    if (sorting[column.key] === 'asc') {
      setSorting({[column.key]: 'desc'});
      if (props.onSort) {
        const sortingDefinition = column.sorting ? column.sorting('desc') : {[column.key]: 'desc'};
        props.onSort(sortingDefinition);
      }
      return;
    }
    setSorting(null);
    if (props.onSort) {
      props.onSort(null);
    }
  }, [props, sorting]);

  const openRow = (event: React.MouseEvent, row: any) => {
    const target = event.target as Element;
    if (target.closest("a, button, [data-row-action]") || window.getSelection()?.toString()) return;
    props.onRowClick?.(row);
  };

  const showColumns = () => {
    return props.columns.map((column: ColumnInterface, index: number) => (
      <TableCell key={index} className={column.sticky && STICKY_CLASS[column.sticky]} align={column.textAlign ?? undefined} onClick={column.skipSorting ? undefined : () => handleSort(column)} style={{cursor: column.skipSorting ? 'default' : 'pointer'}}>
        {column.label}
        {sorting !== null && sorting[column.key] === 'asc' && (<BsChevronUp />)}
        {sorting !== null && sorting[column.key] === 'desc' && (<BsChevronDown />)}
      </TableCell>
    ));
  };

  const showRow = (row: any) => {
    return props.columns.map((column: ColumnInterface, index: number) => {
      if (column.type === columnTypes.DELETE) {
        return (
          <TableCell
            key={index}
            align={column.textAlign ?? undefined}
            sx={{ width: "75px" }}
            data-row-action
          >
            <BsTrash3Fill
              color="red"
              size={20}
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (column.actionFn) {
                  column.actionFn(row);
                } else if (props.onDelete) {
                  props.onDelete(row.id);
                }
              }}
            />
          </TableCell>
        );
      } else if (column.type === columnTypes.EDIT) {
        return (
          <TableCell
            key={index}
            align={column.textAlign ?? undefined}
            sx={{ width: "75px" }}
            data-row-action
          >
            <BsFillPencilFill
              color={colors.grey[500]}
              size={20}
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (column.actionFn) {
                  column.actionFn(row);
                } else if (props.onDelete) {
                  props.onDelete(row.id);
                }
              }}
            />
          </TableCell>
        );
      } else if (column.type === columnTypes.DETAILS) {
        return (
          <TableCell
            key={index}
            align={column.textAlign ?? undefined}
            sx={{ width: "75px" }}
            data-row-action
          >
            <BsEyeFill
              color={colors.grey[500]}
              size={20}
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (column.actionFn) {
                  column.actionFn(row);
                }
              }}
            />
          </TableCell>
        );
      }

      return (
        <TableCell
          key={index}
          className={column.sticky && STICKY_CLASS[column.sticky]}
          align={column.textAlign ?? undefined}
          sx={{ minWidth: column.minWidth ?? "200px" }}
        >
          {(column.rowRender && column.rowRender(row)) ??
            _.get(row, column.key)}
        </TableCell>
      );
    });
  };

  const showRows = () => {
    if (!props.rows.length) {
      if (props.compact) {
        return (
          <TableRow>
            <TableCell colSpan={props.columns.length} align="center" sx={{ py: 6, color: "text.secondary" }}>
              {props.emptyText ?? "No records found."}
            </TableCell>
          </TableRow>
        );
      }
      return (
        <TableRow sx={{ display: "block", pt: 3, height: 60 }}>
          No Records Found.
        </TableRow>
      );
    }

    return props.rows.map((row, rowIndex) => {
      return (
        <TableRow
          key={rowIndex}
          hover={Boolean(props.onRowClick)}
          onClick={props.onRowClick ? (event) => openRow(event, row) : undefined}
          sx={props.onRowClick ? { cursor: "pointer" } : undefined}
        >
          {showRow(row)}
        </TableRow>
      );
    });
  };

  return (
    <>
      {!props.compact && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={props.total}
          rowsPerPage={props.rowsPerPage}
          page={props.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <Box
        sx={{
          overflow: "auto",
          maxHeight: props.compact ? "max(420px, calc(100vh - 220px))" : 600,
          ...(props.compact ? COMPACT_SX : {}),
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>{showColumns()}</TableRow>
          </TableHead>
          <TableBody>{showRows()}</TableBody>
        </Table>
        {!props.compact && <Divider />}
      </Box>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={props.total}
        rowsPerPage={props.rowsPerPage}
        page={props.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={
          props.compact
            ? {
                borderTop: "1px solid #eef1f5",
                // The pager's words do not fit a phone beside its buttons.
                "& .MuiTablePagination-selectLabel": { display: { xs: "none", sm: "block" } },
                "& .MuiTablePagination-toolbar": { px: { xs: 1, sm: 2 } },
                "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
                "& .MuiTablePagination-input": { mr: { xs: 1, sm: 4 }, ml: { xs: 0, sm: 1 } },
                "& .MuiTablePagination-actions": { ml: { xs: "auto", sm: 2.5 } },
              }
            : undefined
        }
      />
    </>
  );
};
export { CustomTable as Table };
